import { ERROR_CODES } from '@pams/shared/constants';
import type { ApiResponse } from '@pams/shared/types';
import { mintRotationKey } from '../auth/rotationKey';
import type { TokenStore } from '../auth/tokenStore';
import { ApiError } from './apiError';

/**
 * The typed API client (CLAUDE.md §8).
 *
 * Every response is the shared envelope, so this is the one place that unwraps it:
 * callers receive `data` or an `ApiError`, and no component ever writes
 * `if (response.success)`.
 *
 * ⚠️ [amended P5b] A FACTORY, NOT A MODULE. There is exactly one of these per app, and
 *  `web/src/lib/api.ts` and `pwa/src/lib/client.ts` are the two places that call it.
 *  See `auth/tokenStore.ts`'s header for why the state is an instance: the mutable
 *  singletons below — the single-flight promise and the auth-lost handler — are the same
 *  hazard as the token mirror, and splitting them is the failure this shape prevents.
 */

/**
 * ⚠️ [C5b §3] A LIST IS SENT AS REPEATED KEYS (`?status=A&status=B`), NEVER `String(list)`.
 * The dashboard's status filter shows WORDS and one word covers two statuses; the list schema
 * reads a repeated key as a list ([P6d]'s `z.preprocess`), while `A,B` is one value it refuses.
 */
export type QueryValue = string | number | boolean | readonly string[] | undefined | null;

export type RequestOptions = {
  query?: Record<string, QueryValue>;
  body?: unknown;
  signal?: AbortSignal;
};

/**
 * ⚠️ [C1b §6a] `timeoutMs` IS PRIVATE, AND THE PUBLIC SURFACE IS UNCHANGED.
 *
 * Only `/auth/refresh` passes one — see the deadline block in `request`. Keeping it off
 * `RequestOptions` means no caller can set a wall-clock deadline on an ordinary read, and a
 * later pass that wants one opts that path in rather than inventing a second mechanism.
 */
type InternalRequestOptions = RequestOptions & { timeoutMs?: number };

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C1b §6a] THESE TWO NUMBERS ARE BOUNDED BY `refreshLock.MAX_HOLD_MS`, AND THE
 *  ARITHMETIC IS ASSERTED RATHER THAN COMMENTED.
 *
 *  The retry runs INSIDE the same `refreshLock.run()` callback, so the lock is held across
 *  both attempts and no second tab can pick up the un-rotated parent in between. That is only
 *  safe while the worst case stays under the point at which a holder becomes STEALABLE:
 *
 *      REFRESH_TIMEOUT_MS × REFRESH_ATTEMPTS  =  24 000  <  MAX_HOLD_MS (30 000)
 *
 *  ⚠️ **THREE ATTEMPTS WOULD BREAK IT** — 36 000 > 30 000 — so a waiter could refresh
 *  UNGUARDED while a legitimate holder was still mid-retry, which is the very race
 *  `refreshLock.ts` exists to prevent, reached through the escape hatch. `refreshLock.test.ts`
 *  asserts the inequality, which is that file's own idiom for a derived constant.
 *
 *  ⚠️ **AND THE LOCK'S OWN CONSTANTS DO NOT MOVE.** `MAX_HOLD_MS` is the point at which a
 *  WEDGED holder stops renewing, and a wedged holder is still possible — a suspended tab,
 *  storage gone slow. A bounded fetch bounds the NETWORK, not the process.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const REFRESH_TIMEOUT_MS = 12_000;
export const REFRESH_ATTEMPTS = 2;

export type ApiClient = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body'>) => Promise<T>;
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) => Promise<T>;
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) => Promise<T>;
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) => Promise<T>;
  delete: <T>(path: string, options?: Omit<RequestOptions, 'body'>) => Promise<T>;
  /** ⚠️ [C2b §3] The one non-envelope read. See the implementation for why it is not a flag. */
  getBlob: (path: string) => Promise<{ blob: Blob; filename: string | null }>;
};

/**
 * ⚠️ [amended C1a] WHY THE SESSION ENDED, IN THE ONLY THREE FLAVOURS THE CLIENT CAN
 * HONESTLY TELL APART — AND IT IS NOT AN ERROR CODE.
 *
 * `ERROR_CODES` is a CLOSED SET and 401 `UNAUTHORIZED` already covers a revocation, so
 * nothing is minted on the wire. The server's `SESSION_INVALID_MESSAGE` also stays
 * uniform across expired / malformed / revoked / replayed, because telling an attacker
 * their replay was detected tells them the legitimate holder is still active
 * (`api/src/services/token.service.ts`). This discrimination is entirely CLIENT-SIDE and
 * uses only what the caller already holds, so it discloses nothing new.
 *
 * A discriminated union rather than a string, so each app has to DECIDE what to render
 * and so the server's own sentence can ride on the one arm entitled to carry it.
 */
export type AuthLostReason =
  /** A 401 from `/auth/refresh`. Expired, revoked or replayed — deliberately indistinguishable. */
  | { kind: 'session-ended' }
  /**
   * A 403 carrying `DASHBOARD_FORBIDDEN_MESSAGE`. The one safe disclosure, on the argument
   * already made for `WRONG_SURFACE`: it describes the caller's OWN authorisation, which
   * they hold, and names no role, scope, record or person.
   */
  | { kind: 'forbidden'; message: string }
  /** This tab held a refresh token and another context removed it — somebody signed out. */
  | { kind: 'signed-out-elsewhere' };

export type AuthClient = {
  api: ApiClient;
  tokens: TokenStore;
  /** At most one `/auth/refresh` is ever in flight. Resolves to the new access token, or undefined. */
  refreshAccessToken: () => Promise<string | undefined>;
  /**
   * Called when a refresh fails and the session is genuinely over. Set by the app's auth
   * provider — this is how the client tells React the user is signed out without
   * importing React.
   *
   * ⚠️ [amended C1a] It now receives WHY. A `() => void` is still assignable, so every
   * existing call site compiles unchanged.
   */
  setAuthFailureHandler: (handler: ((reason: AuthLostReason) => void) | undefined) => void;
};

export type AuthClientOptions = {
  /**
   * Resolved per call rather than captured at construction.
   *
   * The cost is one property read; the benefit is that a test can change the configured
   * base URL without having to reset module state, and nothing depends on this module
   * being constructed after `runtime-config.js` has run.
   */
  baseUrl: () => string;
  tokens: TokenStore;
  /**
   * ⚠️ [P5b] THE ONE CHANNEL BY WHICH A RESPONSE'S **HEADERS** REACH A CALLER, AND IT EXISTS
   * FOR EXACTLY ONE REQUIREMENT.
   *
   * This client unwraps the envelope and hands back `data`, which is the whole point of it —
   * no component ever writes `if (response.success)`. That also means headers are discarded,
   * and normally they should be.
   *
   * The officer PWA's service worker may serve a duty from cache when the network fails, and
   * §7's rule is that duty data is NEVER served from cache without a visible stale marker and
   * a timestamp. The service worker stamps `X-PAMS-Cached-At` on such a response; without a
   * hook here, the app has no honest way to learn it — `dataUpdatedAt` from the query cache
   * would report the moment the CACHED body arrived, which is a fresher time than the data
   * actually is, i.e. precisely the lie the rule exists to prevent.
   *
   * ⚠️ IT IS OBSERVATION ONLY. It cannot alter the response, cannot fail the request, and its
   * return value is ignored; anything it throws would surface as a network error, so it must
   * not throw. `web` passes nothing and behaves exactly as before.
   */
  onResponse?: (path: string, response: Response) => void;
};

/**
 * Endpoints whose own 401 must NOT trigger a refresh-and-retry.
 *
 * `/auth/refresh` recursing on itself is the obvious one; the OTP endpoints return 401
 * for a wrong code, and retrying that after a token refresh is nonsense.
 */
const NO_RETRY_PATHS = new Set(['/auth/refresh', '/auth/request-otp', '/auth/verify-otp', '/auth/logout']);

export const createAuthClient = ({ baseUrl, tokens, onResponse }: AuthClientOptions): AuthClient => {
  let onAuthLost: ((reason: AuthLostReason) => void) | undefined;

  /**
   * ⚠️ SINGLE-FLIGHT REFRESH, WITHIN THIS TAB. The `??=` is the whole mechanism.
   *
   * Twenty components mount at once, twenty requests 401 at once, and every one of them
   * awaits THIS promise — so exactly one POST /auth/refresh goes out. Without it, twenty
   * refreshes race, nineteen of them present a token the first one has already rotated,
   * and the server correctly reads that as REUSE and revokes the entire family. The user
   * is silently signed out by their own dashboard loading.
   *
   * The usual "fix" for that is a grace window on the server, which weakens the exact
   * control it was protecting. This is the right place to solve it.
   *
   * ⚠️ [amended C1a] AND IT IS ONLY HALF THE PROBLEM. This is closure state, so it covers
   * ONE tab; a second tab is a second closure over the same `localStorage` and races the
   * first exactly as twenty components once did. `tokens.refreshLock` is the other half —
   * see `auth/refreshLock.ts`, and see `refreshAccessToken` below for why the ORDER of
   * the token read against the lock acquisition is the entire fix.
   */
  let refreshInFlight: Promise<string | undefined> | undefined;

  const buildUrl = (path: string, query?: Record<string, QueryValue>): string => {
    if (!query) return `${baseUrl()}${path}`;

    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (Array.isArray(value)) {
        /* An empty list adds nothing — it is "no filter", exactly as an empty string is. */
        for (const member of value as readonly string[]) if (member !== '') params.append(key, member);
      } else if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }

    const search = params.toString();
    const base = baseUrl();
    return search ? `${base}${path}?${search}` : `${base}${path}`;
  };

  const request = async <T>(
    method: string,
    path: string,
    options: InternalRequestOptions = {},
    /** Private. Not exposed on the `api` object — a retry must never be caller-controlled. */
    retryOnUnauthorized = true,
  ): Promise<T> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    /*
     * ⚠️ [C2b §3] A `FormData` BODY CARRIES NO `Content-Type` FROM US, AND IS NOT STRINGIFIED.
     *
     * A multipart body's `Content-Type` contains a BOUNDARY that only the runtime knows —
     * `multipart/form-data; boundary=----WebKitFormBoundary…`. Setting the header ourselves
     * omits it, and the server then cannot find where one part ends: busboy raises
     * `Unsupported content type` and the upload fails with a message about the wrong thing.
     * Leaving it unset is what makes `fetch` fill it in, boundary and all.
     */
    const isMultipart = typeof FormData !== 'undefined' && options.body instanceof FormData;
    if (options.body !== undefined && !isMultipart) headers['Content-Type'] = 'application/json';

    const token = tokens.getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    /*
     * ─────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [C1b §6a] A DEADLINE, ON `/auth/refresh` AND NOTHING ELSE.
     *
     *  Three reasons, in order of weight: it is the only request whose lost REPLY costs a
     *  CREDENTIAL rather than a spinner; it is the only one whose duration is an arithmetic
     *  constraint on another mechanism (`refreshLock`'s `MAX_HOLD_MS`); and a blanket
     *  wall-clock deadline is not an idle timeout, so it would abort a slow-but-progressing
     *  list on precisely the 2G handset this exists for.
     *
     *  ⚠️ **`AbortController` + `setTimeout`, NEVER `AbortSignal.timeout`.** That schedules on
     *  an internal timer `vi.useFakeTimers()` does not control, which would make the retry
     *  path untestable in the unit suite. And NEVER `AbortSignal.any`, whose availability is
     *  divergence #28 in miniature — a listener costs one line and cannot be a platform
     *  question.
     *
     *  ⚠️ **THE TIMER IS CLEARED AROUND `response.json()` TOO.** Aborting a signal tears down
     *  the body stream, so clearing between the two is a race that shows up as a truncated
     *  payload rather than as a timeout.
     *
     *  ⚠️ **[FIX-204] AND ON THE SUCCESS PATH, WHICH IT WAS NOT.** This paragraph was true of
     *  the two failure arms and of nothing else, so a request that SUCCEEDED left its timer
     *  pending — the sentence described an intention rather than the code beneath it.
     * ─────────────────────────────────────────────────────────────────────────────────
     */
    const deadline = options.timeoutMs === undefined ? undefined : new AbortController();
    const timer =
      deadline === undefined
        ? undefined
        : setTimeout(() => {
            deadline.abort();
          }, options.timeoutMs);
    if (deadline && options.signal) {
      options.signal.addEventListener(
        'abort',
        () => {
          deadline.abort();
        },
        { once: true },
      );
    }
    const signal = deadline?.signal ?? options.signal;

    let response: Response;
    try {
      response = await fetch(buildUrl(path, options.query), {
        method,
        headers,
        /**
         * No cookies are used for authentication (see tokenStore.ts), so this is not
         * `include` — and that is what makes a CROSS-ORIGIN `API_BASE_URL` work without
         * ceremony: the bearer token rides in a header, which CORS allows once the origin
         * is on the server's allowlist. Nothing here depends on being same-origin.
         */
        credentials: 'same-origin',
        ...(options.body === undefined
          ? {}
          : { body: isMultipart ? (options.body as FormData) : JSON.stringify(options.body) }),
        ...(signal ? { signal } : {}),
      });
    } catch (error) {
      if (timer !== undefined) clearTimeout(timer);
      /*
       * Network failure, DNS, offline, CORS rejection — or our own deadline. All of them are
       * "we do not know what the server did", which is exactly what `status === 0` means to
       * the refresh path below: it must NOT clear the credential.
       */
      throw new ApiError(0, ERROR_CODES.SERVICE_UNAVAILABLE, 'Could not reach the server', error);
    }

    onResponse?.(path, response);

    /*
     * ─── ⚠️ [FIX-204] A SUCCESSFUL RESPONSE WITH NO BODY IS AN ANSWER, NOT A PARSE FAILURE ──
     *
     *  `response.json()` REJECTS on an empty body — MEASURED on this runtime:
     *  `new Response(null, {status: 204}).json()` throws `Unexpected end of JSON input`. So a
     *  bodyless 2xx used to fall into the catch below and surface as
     *  `Unexpected non-JSON response (204)`, which blames a proxy for a request the server
     *  completed correctly and sends the reader to entirely the wrong place.
     *
     *  ⚠️ THIS IS THE FLOOR, NOT THE FIX. The fix was on the SERVER: `deletePolicyVersion` and
     *  `directoryCall` both returned 204 and both now answer `200 + ok(null)`, so every route
     *  this API registers is once again an envelope. This branch exists because the CLIENT must
     *  not mistranslate a legitimate HTTP response — a proxy's 204, a 304, or the next endpoint
     *  somebody writes — and because the message it used to produce was actively misleading.
     *
     *  ⚠️ IT IS KEYED ON THE STATUS ALONE, NOT ON `Content-Length` OR `response.ok`, AND THE
     *  NARROWER TEST IS THE BETTER ONE. 204 is the one status the spec forbids a body — the
     *  runtime enforces it, `new Response('', {status: 204})` throws `Invalid response status
     *  code 204` — so the status IS the fact, with nothing to infer from a header. A
     *  `Content-Length: 0` leg would additionally have been speculative: no route in this API
     *  sends one, and §9's no-dead-tooling rule reaches a branch as much as a dependency.
     *
     *  ⚠️ AND IT IS A SUCCESS STATUS BY CONSTRUCTION, so a bodyless FAILURE still throws. A 500
     *  with an empty body is a real fault and must not be swallowed into `undefined`.
     *
     *  ⚠️ `undefined as T` IS A DELIBERATE HOLE AND IT IS THE SMALLEST ONE AVAILABLE. There is
     *  no value of `T` for a response with no body; the alternative is widening every caller's
     *  return type to `T | undefined`, which would put a narrow at ~200 call sites to describe
     *  a case none of them can reach now that the API always sends an envelope.
     */
    if (response.status === 204) {
      if (timer !== undefined) clearTimeout(timer);
      return undefined as T;
    }

    let payload: ApiResponse<T>;
    try {
      payload = (await response.json()) as ApiResponse<T>;
    } catch {
      if (timer !== undefined) clearTimeout(timer);
      // A non-JSON body means something upstream answered instead of the API — a proxy
      // error page, say. Reporting the status is more useful than a JSON parse error.
      throw new ApiError(
        response.status,
        ERROR_CODES.INTERNAL_ERROR,
        `Unexpected non-JSON response (${response.status})`,
      );
    }

    /*
     * ⚠️ [FIX-204] THE SUCCESS ARM'S `clearTimeout`, WHICH WAS MISSING ENTIRELY.
     *
     * The timer was cleared in both FAILURE arms (`fetch` rejected, `.json()` threw) and on no
     * path where the request succeeded — so every successful `/auth/refresh`, the one caller
     * that passes `timeoutMs`, left a 12-second abort timer pending. Harmless in a browser
     * because the body has already been read; not harmless under `vi.useFakeTimers`, and §1's
     * "`pnpm test` exits on its own" is a property a dangling handle is exactly how you lose.
     *
     * ⚠️ IT IS AFTER `response.json()` RESOLVES, NEVER BEFORE — the header above says why:
     * aborting tears down the body stream, so clearing between the two is a race that shows up
     * as a truncated payload rather than as a timeout.
     */
    if (timer !== undefined) clearTimeout(timer);

    if (!payload.success) {
      if (response.status === 401 && retryOnUnauthorized && !NO_RETRY_PATHS.has(path)) {
        const refreshed = await refreshAccessToken();
        // At most once, and the original request never reached a handler — it was rejected
        // at the auth middleware — so retrying a POST is not a double submit.
        if (refreshed) return request<T>(method, path, options, false);
      }
      throw new ApiError(response.status, payload.error.code, payload.error.message, payload.error.details);
    }

    return payload.data;
  };

  /**
   * ⚠️ [C1b §6a] EXACTLY ONE RETRY, AND ONLY FOR A REQUEST THAT NEVER REACHED THE SERVER.
   *
   * `status === 0` is the one class this may retry: a network failure or our own deadline,
   * where nothing is known about what the server did. Any parsed envelope — a 401, a 403, a
   * 500 — is an ANSWER, and answering it twice is how a refusal becomes two.
   *
   * The count is pinned rather than looped: see `REFRESH_ATTEMPTS`' header for the arithmetic
   * that makes two safe and three not.
   */
  async function withRetries<T>(attempt: () => Promise<T>): Promise<T> {
    let lastError: unknown;
    for (let tries = 0; tries < REFRESH_ATTEMPTS; tries += 1) {
      try {
        return await attempt();
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 0) throw error;
        lastError = error;
      }
    }
    throw lastError;
  }

  function refreshAccessToken(): Promise<string | undefined> {
    /*
     * ⚠️ [amended C1a] READ BEFORE THE LOCK ONLY TO ANSWER "DID THIS TAB EVER HAVE ONE".
     *
     * It is NEVER the token that gets presented — see below. Its only job is to tell an
     * anonymous tab (nothing to lose) apart from a tab whose credential another context
     * removed while it waited, which is a real sign-out and must be reported as one.
     */
    const heldBefore = tokens.getRefreshToken() !== undefined;

    refreshInFlight ??= tokens.refreshLock
      .run(async (): Promise<string | undefined> => {
        /*
         * ─────────────────────────────────────────────────────────────────────────────
         *  ⚠️ [amended C1a] RE-READ **AFTER** ACQUIRING THE LOCK. THIS LINE IS THE FIX.
         *
         *  Reading the refresh token before waiting is precisely the reported bug: two
         *  tabs both read the parent, queue up, and the second presents a token the first
         *  has already spent. Reading it here means the waiter picks up the SUCCESSOR the
         *  previous holder just wrote, so its rotation is ordinary and valid rather than
         *  a replay the server is right to punish.
         *
         *  It also means the lock is a MUTEX and not a shared-promise cache: the waiter
         *  performs its own rotation. It cannot receive the winner's ACCESS token, which
         *  lives in the winner's per-tab `sessionStorage`. See `refreshLock.ts`.
         * ─────────────────────────────────────────────────────────────────────────────
         */
        const refreshToken = tokens.getRefreshToken();
        if (!refreshToken) {
          /*
           * Nothing to present. If this tab HELD one a moment ago, another context
           * cleared it — that is a sign-out and the app must be told, or it sits on a
           * dead session surfacing raw query errors. If it never held one, this is an
           * anonymous 401 and nothing has happened.
           */
          if (heldBefore) onAuthLost?.({ kind: 'signed-out-elsewhere' });
          return undefined;
        }

        /*
         * ─────────────────────────────────────────────────────────────────────────────
         *  ⚠️ [C1b §6a] ONE KEY FOR THE WHOLE ATTEMPT-CHAIN, READ ONCE INTO A LOCAL.
         *
         *  A key already pending is a rotation whose answer this client never saw — from a
         *  timeout, a reload, or an OS app-kill. Presenting the SAME one re-serves the SAME
         *  successor instead of reading a spent token as a replay.
         *
         *  ⚠️ IT IS HELD IN A LOCAL, not re-read per attempt: another tab overwriting the slot
         *  must not change what THIS tab presents halfway through its own retry.
         *
         *  ⚠️ AND IT IS WRITTEN BEFORE THE REQUEST GOES OUT. A key minted and sent but never
         *  persisted is a key the retry cannot find, which is the whole mechanism missing.
         *
         *  ⚠️ NO CSPRNG ⇒ NO KEY, and the body is byte-identical to what shipped before this
         *  pass. `rotationKey.ts` explains why there is no `Math.random` fallback.
         * ─────────────────────────────────────────────────────────────────────────────
         */
        const rotationKey = tokens.getPendingRotationKey() ?? mintRotationKey();
        if (rotationKey) tokens.setPendingRotationKey(rotationKey);
        const body = { refreshToken, ...(rotationKey ? { rotationKey } : {}) };

        try {
          const session = await withRetries(() =>
            request<{ accessToken: string; refreshToken: string }>(
              'POST',
              '/auth/refresh',
              { body, timeoutMs: REFRESH_TIMEOUT_MS },
              false,
            ),
          );
          // Rotation: the server returns a NEW refresh token and retires the old one, so
          // both must be stored together or the next refresh presents a retired token.
          // `setTokens` also clears the pending key — see `tokenStore.ts`.
          tokens.setTokens({ accessToken: session.accessToken, refreshToken: session.refreshToken });
          return session.accessToken;
        } catch (error) {
          /*
           * ─────────────────────────────────────────────────────────────────────────────
           *  ⚠️ [P5c] A NETWORK FAILURE IS NOT A REVOCATION, AND TREATING IT AS ONE SIGNED
           *  AN OFFICER OUT FOR STANDING IN A BASEMENT.
           *
           *  This was a bare `catch`. `request` above throws `ApiError(0, …)` for a network
           *  failure, DNS, offline or a CORS rejection — the SAME path as "the server read
           *  your refresh token and revoked the family" — and the response was to delete a
           *  36-hour credential from `localStorage`.
           *
           *  `AuthProvider` calls this on every cold start where a refresh token exists, so
           *  the measured consequence was: open the app with no signal, and you are signed
           *  out — with no network to sign back in on. Both surfaces had it; `web` shares
           *  this client.
           *
           *  ⚠️ AND IT IS WHY P5c's OFFLINE QUEUE NEEDED THIS FIXED FIRST. §7d requires a
           *  queued marking to survive the refresh FAILING. The queue would have survived;
           *  the credential to send it with would not, which is the same loss by another
           *  route.
           *
           *  `status === 0` means we never reached the server, so the refresh token is
           *  exactly as valid as it was a second ago. Keep it, report failure, and let the
           *  caller try again when there is a network. Every OTHER failure — a 401 on a
           *  reused token, a malformed body, a 500 — still clears and still signals, because
           *  those are answers.
           * ─────────────────────────────────────────────────────────────────────────────
           */
          if (error instanceof ApiError && error.status === 0) return undefined;

          tokens.clearTokens();
          /*
           * ⚠️ [amended C1a] WHY, NOT MERELY THAT. A 403 here is `rotateSession` refusing
           * a dashboard session to an officer whose role no longer qualifies, and its
           * message is the server's own `DASHBOARD_FORBIDDEN_MESSAGE` — safe to show
           * verbatim, because it describes the caller's own authorisation. Everything
           * else is the deliberately uniform 401 and gets the generic sentence.
           */
          onAuthLost?.(
            error instanceof ApiError && error.status === 403
              ? { kind: 'forbidden', message: error.message }
              : { kind: 'session-ended' },
          );
          return undefined;
        }
      })
      .finally(() => {
        // Cleared once the rotation SETTLES, so the next 401 after this starts a fresh
        // attempt rather than awaiting a promise that has already resolved to undefined.
        refreshInFlight = undefined;
      });

    return refreshInFlight;
  }

  const api: ApiClient = {
    get: <T>(path: string, options?: Omit<RequestOptions, 'body'>) => request<T>('GET', path, options),
    post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
      request<T>('POST', path, { ...options, body }),
    patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
      request<T>('PATCH', path, { ...options, body }),
    put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
      request<T>('PUT', path, { ...options, body }),
    delete: <T>(path: string, options?: Omit<RequestOptions, 'body'>) => request<T>('DELETE', path, options),
    getBlob: async (path: string): Promise<{ blob: Blob; filename: string | null }> => {
      /*
       * ─── ⚠️ [C2b §3] THE ONE RESPONSE THIS CLIENT DOES NOT PARSE AS JSON ────────────────
       *
       *  A download cannot go through `request()`: that function's whole contract is "unwrap
       *  the envelope", and it calls `response.json()` unconditionally — on a PDF that throws
       *  and reports a non-JSON response, which is exactly what a PDF is.
       *
       *  ⚠️ IT IS A SEPARATE METHOD RATHER THAN A FLAG ON `get`, because the RETURN TYPE
       *  differs. A `responseType` option would make `get<T>` return `T | Blob` and every one
       *  of its ~200 call sites would have to narrow — [P5c.0]'s rule for this family: a
       *  parameter that changes what comes back belongs in the signature, not in an options bag.
       *
       *  ⚠️ AND IT DOES NOT RETRY ON 401. `request`'s single-flight refresh is closure state
       *  this function deliberately does not reach into; a download that lands on an expired
       *  token fails once and the next navigation refreshes. Duplicating the retry here would
       *  be a SECOND rotation path, and `clientCoreIsolation.test.ts` asserts there is one.
       *
       *  ⚠️ A REFUSAL IS STILL AN ENVELOPE. The server answers 404/403 as JSON, so a non-OK
       *  status is parsed the ordinary way and surfaces as a real `ApiError` with the server's
       *  own sentence rather than "download failed".
       */
      const token = tokens.getAccessToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      let response: Response;
      try {
        response = await fetch(buildUrl(path), { method: 'GET', headers, credentials: 'same-origin' });
      } catch (error) {
        throw new ApiError(0, ERROR_CODES.SERVICE_UNAVAILABLE, 'Could not reach the server', error);
      }

      if (!response.ok) {
        let message = `Download failed (${String(response.status)})`;
        let code: string = ERROR_CODES.INTERNAL_ERROR;
        try {
          const payload = (await response.json()) as ApiResponse<never>;
          if (!payload.success) {
            message = payload.error.message;
            code = payload.error.code;
          }
        } catch {
          /* Not an envelope — a proxy page, say. The status is the honest answer. */
        }
        throw new ApiError(response.status, code, message);
      }

      /*
       * ⚠️ THE FILENAME COMES FROM `Content-Disposition`, WHICH THE SERVER SANITISED. Parsing
       * it here rather than passing one in means the browser's Save dialog shows the name the
       * record holds, even after somebody renames the row — and the quotes are stripped
       * because `sanitiseFilename` guarantees the value contains none of its own.
       */
      const disposition = response.headers.get('Content-Disposition') ?? '';
      const match = /filename="([^"]*)"/.exec(disposition);
      return { blob: await response.blob(), filename: match?.[1] ?? null };
    },
  };

  return {
    api,
    tokens,
    refreshAccessToken,
    setAuthFailureHandler: (handler) => {
      onAuthLost = handler;
    },
  };
};
